/**
 * @fileoverview Shared ContactSearchTypeahead — typeahead with create-fallback.
 * @module ui-admin/components
 *
 * Responsibilities:
 * - Single-input contact search that filters the system contact pool live.
 * - On selection, optionally prompt the host to link the contact to the
 *   entity (mirrors ContactsPanel "Also add to {entity}?" pattern).
 * - On no-match, surface "Create '<name>' as new contact" inline create form.
 *
 * Dependencies:
 * - react, lucide-react (peer)
 * - @firesquirrel/core-domain (ContactFormValues)
 * - sibling ContactInlineForm (./shared-forms/ContactInlineForm)
 * - sibling useContactRoles (host-neutral fallback)
 *
 * Architecture:
 * - Presentation-only; persistence is delegated via onCreateContact /
 *   onLinkToEntity / onSelect callbacks.
 * - Local-first hosts must inject availableRoles from their Dexie-backed
 *   useContactRoles hook (tenant-web, mobile-pwa). The default fallback
 *   uses ui-admin's useContactRoles, which is acceptable only inside
 *   non-persistent preview surfaces (e.g. system-web template canvas).
 *
 * Notes:
 * - Promoted from web/tenant-web/src/components/ContactSearchTypeahead.tsx.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { X, Plus, UserCheck, Search } from 'lucide-react';
import { useContactRoles } from '../hooks/useContactRoles';
import type { GridTheme } from '../contexts/ThemeContext';
import { ContactInlineForm } from './shared-forms/ContactInlineForm';
import type { ContactFormValues, AddressProximity } from '@firesquirrel/core-domain';
import type { PanelContact, NewContactInput } from './ContactsPanel';

// ============================================================
// Helpers — extract primary phone/email for display
// ============================================================

function _primaryPhone(c: PanelContact): string {
  const p = c.phoneNumbers.find(p => p.isPrimary) ?? c.phoneNumbers[0];
  return p?.number ?? '';
}

function _primaryEmail(c: PanelContact): string {
  const e = c.emails.find(e => e.isPrimary) ?? c.emails[0];
  return e?.email ?? '';
}

// ============================================================
// Props
// ============================================================

export interface ContactSearchTypeaheadProps {
  /** Entity-scoped contacts pool shown by default (DFE-019) */
  contactPool: PanelContact[];
  /**
   * Full system-wide contacts pool for fallback search (DFE-019).
   * When provided, the typeahead searches this pool when the user types a
   * query that has no matches in `contactPool`, showing system results in a
   * separate "System contacts" section. This satisfies the acceptance
   * criterion: "entity-linked contacts by default, with option to search
   * system pool."
   */
  systemContactPool?: PanelContact[];
  /** Contacts currently linked to this case (for badge display and dedup) */
  caseContacts: PanelContact[];
  /** Contacts that belong to the entity (for entity badge and prompt suppression) */
  entityContacts?: PanelContact[];
  /** Entity name for display in prompts */
  entityName: string;
  /** Active theme */
  theme: GridTheme;
  /** Currently selected contact (controlled) */
  selectedContact: PanelContact | null;
  /** Called when user selects a contact from the pool */
  onSelect: (contact: PanelContact) => void;
  /** Called when user clears the selected contact */
  onClear: () => void;
  /** Called when user confirms linking a contact to the entity (with role) */
  onLinkToEntity?: (contact: PanelContact, role: string, contactType: 'responsible' | 'contact') => void;
  /** Called when user unlinks a contact from the entity */
  onUnlinkFromEntity?: (contactId: string) => void;
  /** Called when user creates a brand-new contact in the system pool */
  onCreateContact?: (input: NewContactInput) => PanelContact | Promise<PanelContact> | void | Promise<void> | Promise<PanelContact | void>;
  /** Called when user saves edits to a contact */
  onEditContact?: (contactId: string, values: ContactFormValues) => void;
  /** Validation error message */
  error?: string;
  /**
   * Optional list of role display names. When omitted, falls back to
   * ui-admin's useContactRoles. Local-first hosts (tenant-web, mobile-pwa)
   * should inject their Dexie-backed roles list.
   */
  availableRoles?: string[];
  /**
   * Optional public Mapbox client token, threaded to the inline contact form's
   * StructuredAddressField so the typeahead's add/edit flows get the interactive
   * address picker + USPS validation. Omit to disable the picker.
   */
  mapboxToken?: string;
  /**
   * Optional bias point (e.g. the tenant's department coverage centroid) so
   * as-you-type address suggestions near the department rank above equally-
   * valid distant matches. Never excludes results.
   */
  mapboxProximity?: AddressProximity;
  /**
   * DFE-019 (mobile parity): when the entity-scoped `contactPool` is empty and
   * the user hasn't typed a query, fall back to showing `systemContactPool` as
   * the default list instead of a blank dropdown — matching mobile-pwa's
   * ContactPicker, which never presents an empty picker. Only affects the
   * no-query default view; the "System contacts" search section is unchanged.
   * Single toggle point — set false to revert to spec-literal "empty by
   * default, search for system pool" behavior. Defaults to false.
   */
  fallbackToSystemPoolWhenEmpty?: boolean;
}

// ============================================================
// ContactSearchTypeahead
// ============================================================

export function ContactSearchTypeahead({
  contactPool,
  systemContactPool,
  caseContacts,
  entityContacts,
  entityName,
  theme: t,
  selectedContact,
  onSelect,
  onClear,
  onLinkToEntity,
  onUnlinkFromEntity,
  onCreateContact,
  onEditContact,
  error,
  availableRoles: availableRolesProp,
  mapboxToken,
  mapboxProximity,
  fallbackToSystemPoolWhenEmpty = false,
}: ContactSearchTypeaheadProps) {
  const fallbackRoles = useContactRoles();
  const availableRoles = availableRolesProp ?? fallbackRoles;
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Entity link prompt state (Tier 2: system contact not in entity)
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [showNewLinkPrompt, setShowNewLinkPrompt] = useState(false);
  const [pendingContact, setPendingContact] = useState<PanelContact | null>(null);
  const [linkRole, setLinkRole] = useState<string>('');

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const entityContactIds = useMemo(() => new Set((entityContacts ?? []).map(c => c._id)), [entityContacts]);
  const caseContactIds = useMemo(() => new Set(caseContacts.map(c => c._id)), [caseContacts]);

  // IDs in the entity pool — used to exclude entity contacts from system results
  const entityPoolIds = useMemo(() => new Set(contactPool.map(c => c._id)), [contactPool]);

  // Filter pool contacts by query — exclude contacts already on the case.
  // DFE-019 (mobile parity): when the entity-scoped pool is empty and no query
  // is typed, optionally fall back to the system pool so the default view is
  // never blank (matches mobile-pwa's ContactPicker). Gated on
  // `fallbackToSystemPoolWhenEmpty` — the single toggle for this behavior.
  const filteredPool = useMemo(() => {
    const pool = contactPool.filter(c => !caseContactIds.has(c._id));
    if (!query.trim()) {
      if (pool.length === 0 && fallbackToSystemPoolWhenEmpty && systemContactPool) {
        return systemContactPool.filter(c => !caseContactIds.has(c._id)).slice(0, 10);
      }
      return pool.slice(0, 10);
    }
    const q = query.toLowerCase();
    return pool.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.company && c.company.toLowerCase().includes(q)) ||
      (c.title && c.title.toLowerCase().includes(q)) ||
      c.defaultRoles.some(r => r.toLowerCase().includes(q)) ||
      c.phoneNumbers.some(p => p.number.includes(q)) ||
      c.emails.some(e => e.email.toLowerCase().includes(q))
    );
  }, [contactPool, query, caseContactIds, fallbackToSystemPoolWhenEmpty, systemContactPool]);

  // DFE-019: system pool fallback — search the full system pool when the user
  // types a query and entity contacts don't match. Excludes contacts already
  // in the entity pool or already on the case.
  const systemPoolResults = useMemo(() => {
    if (!systemContactPool || !query.trim()) return [];
    const q = query.toLowerCase();
    return systemContactPool.filter(c =>
      !entityPoolIds.has(c._id) &&
      !caseContactIds.has(c._id) &&
      (c.name.toLowerCase().includes(q) ||
        (c.company && c.company.toLowerCase().includes(q)) ||
        (c.title && c.title.toLowerCase().includes(q)) ||
        c.defaultRoles.some(r => r.toLowerCase().includes(q)) ||
        c.phoneNumbers.some(p => p.number.includes(q)) ||
        c.emails.some(e => e.email.toLowerCase().includes(q)))
    ).slice(0, 10);
  }, [systemContactPool, query, entityPoolIds, caseContactIds]);

  const hasNoExactMatch = useMemo(() => {
    if (!query.trim()) return false;
    const q = query.trim().toLowerCase();
    const inEntity = contactPool.some(c => c.name.toLowerCase() === q);
    const inSystem = systemContactPool?.some(c => c.name.toLowerCase() === q) ?? false;
    return !inEntity && !inSystem;
  }, [contactPool, systemContactPool, query]);

  // Select a pool contact — skip if already on the case, check entity linkage
  const handleSelectPool = useCallback((c: PanelContact) => {
    if (caseContactIds.has(c._id)) return;
    const isInEntity = entityContactIds.has(c._id);
    onSelect(c);
    setQuery(c.name);
    setShowDropdown(false);
    setShowAddForm(false);

    if (!isInEntity && onLinkToEntity) {
      setPendingContact(c);
      setLinkRole('');
      setShowLinkPrompt(true);
    }
  }, [caseContactIds, entityContactIds, onSelect, onLinkToEntity]);

  // Link pending contact to entity (with role)
  const confirmLink = useCallback((contact: PanelContact) => {
    if (!linkRole) return;
    onLinkToEntity?.(contact, linkRole, 'contact');
    setShowLinkPrompt(false);
    setShowNewLinkPrompt(false);
    setPendingContact(null);
    setLinkRole('');
  }, [onLinkToEntity, linkRole]);

  // Dismiss prompts
  const dismissPrompt = useCallback(() => {
    setShowLinkPrompt(false);
    setShowNewLinkPrompt(false);
    setPendingContact(null);
    setLinkRole('');
  }, []);

  // Clear selection
  const handleClear = useCallback(() => {
    onClear();
    setQuery('');
    setShowLinkPrompt(false);
    setShowNewLinkPrompt(false);
    setPendingContact(null);
    setLinkRole('');
    setShowEditForm(false);
  }, [onClear]);

  // Edit initial values
  const editInitialValues: ContactFormValues | undefined = selectedContact ? {
    name: selectedContact.name,
    defaultRoles: selectedContact.defaultRoles as ContactFormValues['defaultRoles'],
    company: selectedContact.company || '',
    title: selectedContact.title || '',
    phoneNumbers: selectedContact.phoneNumbers,
    emails: selectedContact.emails,
    address: selectedContact.address || '',
    // Re-hydrate the structured (possibly USPS-validated) mailing address so the
    // edit form's StructuredAddressField shows the saved address instead of
    // starting blank and silently discarding it on the next save.
    mailingAddress: selectedContact.mailingAddress,
  } : undefined;

  const handleEditSave = useCallback((vals: ContactFormValues) => {
    if (!selectedContact) return;
    onEditContact?.(selectedContact._id, vals);
    setShowEditForm(false);
  }, [selectedContact, onEditContact]);

  // Create new contact
  const handleCreate = useCallback(async (vals: ContactFormValues) => {
    const input: NewContactInput = {
      name: vals.name.trim(),
      defaultRoles: vals.defaultRoles,
      company: vals.company.trim() || undefined,
      title: vals.title.trim() || undefined,
      phoneNumbers: vals.phoneNumbers,
      emails: vals.emails,
      address: vals.address.trim() || undefined,
      // Carry the structured (possibly USPS-validated) mailing address through to
      // the host so a contact added during an inspection — e.g. to be the
      // responsible party or a report recipient — keeps a real, mailable address
      // instead of dropping it at the create boundary.
      mailingAddress: vals.mailingAddress ?? undefined,
    };
    const created = await onCreateContact?.(input);
    if (created && onLinkToEntity) {
      onSelect(created);
      setQuery(created.name);
      setPendingContact(created);
      setLinkRole('');
      setShowNewLinkPrompt(true);
    } else if (created) {
      onSelect(created);
      setQuery(created.name);
    }
    setShowAddForm(false);
  }, [onCreateContact, onSelect, onLinkToEntity]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const inputCls = 'w-full px-3 py-2 text-sm border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors';

  return (
    <div>
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowDropdown(true);
            if (selectedContact) {
              onClear();
              setShowLinkPrompt(false);
              setShowNewLinkPrompt(false);
            }
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search contacts..."
          className={`${inputCls} pl-8 ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'} pr-8`}
        />
        {selectedContact && (
          <button
            onClick={() => { handleClear(); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-3 w-3" />
          </button>
        )}

        {/* Dropdown */}
        {showDropdown && !showAddForm && (
          <div ref={dropdownRef} className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
            {filteredPool.length > 0 ? (
              filteredPool.map((c) => {
                const isInEntity = entityContactIds.has(c._id);
                return (
                  <button key={c._id}
                    onClick={() => handleSelectPool(c)}
                    className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                      selectedContact?._id === c._id ? 'bg-blue-50' : ''
                    }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{c.name}</span>
                      <div className="flex items-center gap-1.5">
                        {isInEntity && <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-green-100 text-green-700 font-medium">Entity</span>}
                        {c.defaultRoles.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-500">{c.defaultRoles.join(', ')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {c.company && <span className="text-[11px] text-gray-500">{c.company}</span>}
                      {c.title && <span className="text-[11px] text-gray-400">· {c.title}</span>}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{_primaryPhone(c)} · {_primaryEmail(c)}</div>
                  </button>
                );
              })
            ) : (
              !systemPoolResults.length && <div className="px-3 py-3 text-center text-xs text-gray-500">No contacts match &ldquo;{query}&rdquo;</div>
            )}
            {/* DFE-019: System pool fallback results */}
            {systemPoolResults.length > 0 && (
              <>
                <div className="px-3 py-1.5 border-t border-gray-200 bg-gray-50">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">System contacts</p>
                </div>
                {systemPoolResults.map((c) => (
                  <button key={`sys-${c._id}`}
                    onClick={() => handleSelectPool(c)}
                    className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                      selectedContact?._id === c._id ? 'bg-blue-50' : ''
                    }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{c.name}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-700 font-medium">System</span>
                        {c.defaultRoles.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-500">{c.defaultRoles.join(', ')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {c.company && <span className="text-[11px] text-gray-500">{c.company}</span>}
                      {c.title && <span className="text-[11px] text-gray-400">· {c.title}</span>}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{_primaryPhone(c)} · {_primaryEmail(c)}</div>
                  </button>
                ))}
              </>
            )}
            {/* Tier 3: Add New Contact */}
            {hasNoExactMatch && query.trim().length > 0 && onCreateContact && (
              <button
                onClick={() => { setShowAddForm(true); setShowDropdown(false); }}
                className="w-full text-left px-3 py-2.5 border-t border-gray-200 hover:bg-green-50 transition-colors flex items-center gap-2">
                <Plus className="h-3.5 w-3.5 text-green-600" />
                <span className="text-sm font-medium text-green-700">Add &ldquo;{query.trim()}&rdquo; as new system contact</span>
              </button>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {/* Selected contact chip */}
      {selectedContact && !showEditForm && (
        <div className="mt-2 border border-blue-200 bg-blue-50 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2">
            <UserCheck className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-blue-900">{selectedContact.name}</div>
              <div className="text-[10px] text-blue-600">{selectedContact.defaultRoles.join(', ')}{selectedContact.company ? ` · ${selectedContact.company}` : ''} · {_primaryPhone(selectedContact)}</div>
              {selectedContact.address && <div className="text-[10px] text-blue-500">{selectedContact.address}</div>}
            </div>
            {entityContactIds.has(selectedContact._id)
              ? <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-green-100 text-green-700 font-medium shrink-0">In entity</span>
              : <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 font-medium shrink-0">System only</span>
            }
          </div>
          <div className="flex items-center gap-1 px-2 py-1.5 border-t border-blue-200 bg-blue-100/50">
            <button onClick={handleClear} className="text-[10px] font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-200 px-2 py-0.5 rounded-sm transition-colors">Change</button>
            {onEditContact && (
              <button onClick={() => setShowEditForm(true)} className="text-[10px] font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-200 px-2 py-0.5 rounded-sm transition-colors">Edit</button>
            )}
            {entityContactIds.has(selectedContact._id) && onUnlinkFromEntity && (
              <button onClick={() => onUnlinkFromEntity(selectedContact._id)} className="text-[10px] font-medium text-red-600 hover:text-red-800 hover:bg-red-100 px-2 py-0.5 rounded-sm transition-colors ml-auto">Remove from Entity</button>
            )}
          </div>
        </div>
      )}

      {/* Inline edit contact form */}
      {selectedContact && showEditForm && editInitialValues && (
        <div className="mt-2">
          <ContactInlineForm
            initialValues={editInitialValues}
            theme={t}
            mapboxToken={mapboxToken}
            mapboxProximity={mapboxProximity}
            onSubmit={handleEditSave}
            onCancel={() => setShowEditForm(false)}
            submitLabel="Save Changes"
          />
        </div>
      )}

      {/* Prompt: system contact NOT in entity — offer to add */}
      {showLinkPrompt && pendingContact && (
        <div className="mt-2 border border-amber-200 bg-amber-50 rounded-lg p-3">
          <p className="text-xs text-amber-800 mb-2">
            <strong>{pendingContact.name}</strong> is in the system but not in <strong>{entityName}</strong>. Add them to this entity?
          </p>
          <div className="flex items-center gap-2 mb-2">
            <select
              value={linkRole}
              onChange={(e) => setLinkRole(e.target.value)}
              className="flex-1 text-xs border border-amber-300 rounded-sm px-2 py-1 bg-white focus:ring-2 focus:ring-amber-400 outline-hidden">
              <option value="">-- Select role --</option>
              {availableRoles.map((r: string) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => confirmLink(pendingContact)}
              disabled={!linkRole}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-40 bg-amber-500 text-white hover:bg-amber-600">
              Yes, add to entity
            </button>
            <button onClick={dismissPrompt}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
              Event only
            </button>
          </div>
        </div>
      )}

      {/* Prompt: newly created — also add to entity? */}
      {showNewLinkPrompt && pendingContact && (
        <div className="mt-2 border border-blue-200 bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-800 mb-2">
            <strong>{pendingContact.name}</strong> added to system. Also add to <strong>{entityName}</strong>?
          </p>
          <div className="flex items-center gap-2 mb-2">
            <select
              value={linkRole}
              onChange={(e) => setLinkRole(e.target.value)}
              className="flex-1 text-xs border border-blue-300 rounded-sm px-2 py-1 bg-white focus:ring-2 focus:ring-blue-400 outline-hidden">
              <option value="">-- Select role --</option>
              {availableRoles.map((r: string) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => confirmLink(pendingContact)}
              disabled={!linkRole}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-40 bg-blue-500 text-white hover:bg-blue-600">
              Yes, add to entity
            </button>
            <button onClick={dismissPrompt}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
              Skip
            </button>
          </div>
        </div>
      )}

      {/* Inline add new contact form */}
      {showAddForm && (
        <div className="mt-2">
          <ContactInlineForm
            prefillName={query.trim()}
            theme={t}
            mapboxToken={mapboxToken}
            mapboxProximity={mapboxProximity}
            onSubmit={handleCreate}
            onCancel={() => setShowAddForm(false)}
            submitLabel="Add to System & Select"
          />
        </div>
      )}
    </div>
  );
}
